import { Link } from 'react-router-dom';
import { useContext, useState } from 'react';
import DOMPurify from 'dompurify';
import { CardsContext } from '../context/CardsContext';
import { SiteContentContext } from '../context/SiteContentContext';
import DifficultyIcons from '../components/DifficultyIcons';
import Icon from '../components/Icon';
import BookingModal from '../components/BookingModal';
import QuestionModal from '../components/QuestionModal';
import AutoCarousel from '../components/AutoCarousel';
import FaqPreview from '../components/FaqPreview';
import PriceBanner from '../components/PricePopup';
import { injectIymcsMark } from '../utils/richText';
import { useSeo } from '../hooks/useSeo';
import '../styles/adventures_page.css';

const safe = (html) => ({ __html: injectIymcsMark(DOMPurify.sanitize(html || '')) });

const Adventures = ({ isPreview = false }) => {
  const { cards, productionCards } = useContext(CardsContext);
  const { content } = useContext(SiteContentContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isQuestionOpen, setIsQuestionOpen] = useState(false);

  useSeo({
    title: 'Парусные путешествия',
    description: 'Морские путешествия под парусом по красивейшим местам — от однодневных прогулок до многодневных круизов с опытным капитаном.',
  });

  const source = isPreview ? cards : productionCards;
  const adventures = (source.adventures || []).filter(c => c.isPublished === true);
  const cardLink = (card) => isPreview ? `/preview/adventures/${card.slug || card.id}` : `/adventures/${card.slug || card.id}`;
  const galleryImages = adventures.flatMap(c => c.gallery || []).filter(Boolean);

  const hasAnyPrice = adventures.some(c => c.price);

  return (
    <main className="adventures-page">
      {hasAnyPrice && <PriceBanner isPreview={isPreview} />}
      <section
        className="adventures-hero"
        style={content.adventuresHeroImage ? {
          backgroundImage: `linear-gradient(rgba(0,0,0,0.55),rgba(0,0,0,0.55)), url(${content.adventuresHeroImage})`
        } : {}}
      >
        <div className="hero-content">
          <h1>Парусные путешествия</h1>
          <p className="subtitle">
            Откройте для себя свободу морских просторов с нашей профессиональной командой
          </p>
        </div>
      </section>
      {!content.adventuresFeaturesBelow && (content.adventuresFeaturesTitle || (content.adventuresFeatures || []).length > 0) && (
        <section className="intro-section">
          <div className="intro-content">
            {content.adventuresFeaturesTitle && <h2>{content.adventuresFeaturesTitle}</h2>}
            {content.adventuresFeaturesText && <div className={content.adventuresFeaturesTextWide ? 'intro-features-text--wide' : ''} dangerouslySetInnerHTML={safe(content.adventuresFeaturesText)} />}
            {(content.adventuresFeatures || []).length > 0 && (
              <div className="features-grid">
                {(content.adventuresFeatures || []).map(f => (
                  <div key={f.id} className="feature">
                    <Icon name={f.icon} size={28}/>
                    <h3>{f.title}</h3>
                    <p>{f.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
      <section className="adventures-list">
        <h2>Наши маршруты</h2>
        <div className={`cards-list${adventures.length === 1 ? ' cards-list--single' : ''}`}>
          {adventures.map(card => (
            <Link key={card.id} to={cardLink(card)} className="adventure-card">
              {card.image && (<div className="card-image" style={{ backgroundImage: `url(${card.image})` }}></div>)}
              <div className="card-content">
                <h3>{card.title}</h3>
                <div className="card-meta">
                  {card.duration && (<span><Icon name="clock" size={14}/> {card.duration}</span>)}
                  {card.miles && (<span><Icon name="miles" size={14}/> {card.miles} миль</span>)}
                  {card.price && (<span className="card-price-link"><Icon name="money" size={14}/> {card.price}</span>)}
                  {(card.badges || []).map((b, i) => (
                    <span key={`badge-${i}`}><Icon name={b.icon} size={14}/> {b.text}</span>
                  ))}
                  {card.difficulty > 0 && (
                    <DifficultyIcons difficulty={card.difficulty} sectionKey="adventures" />
                  )}
                </div>
                <div className="card-desc-html" dangerouslySetInnerHTML={safe(card.description)} />
                {card.includes && (
                  <div className="card-highlights">
                    <h4>Что включено:</h4>
                    <ul>
                      {card.includes.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <span className="details-link">Подробнее о маршруте →</span>
              </div>
            </Link>
          ))}
        </div>
      </section>
      {content.adventuresFeaturesBelow && (content.adventuresFeaturesTitle || (content.adventuresFeatures || []).length > 0) && (
        <section className="intro-section">
          <div className="intro-content">
            {content.adventuresFeaturesTitle && <h2>{content.adventuresFeaturesTitle}</h2>}
            {content.adventuresFeaturesText && <div className={content.adventuresFeaturesTextWide ? 'intro-features-text--wide' : ''} dangerouslySetInnerHTML={safe(content.adventuresFeaturesText)} />}
            {(content.adventuresFeatures || []).length > 0 && (
              <div className="features-grid">
                {(content.adventuresFeatures || []).map(f => (
                  <div key={f.id} className="feature">
                    <Icon name={f.icon} size={28}/>
                    <h3>{f.title}</h3>
                    <p>{f.text}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}
      <FaqPreview faqItems={content.faqItems || []} category="adventure" faqPath={isPreview ? '/preview/faq' : '/faq'} />
      {galleryImages.length > 0 && (
        <AutoCarousel images={galleryImages} title="Фотогалерея" interval={4500} />
      )}

      <section className="cta-section">
        <h2>{content.adventuresCtaTitle || 'Остались вопросы?'}</h2>
        {content.adventuresCtaText && <div className={content.adventuresCtaTextWide ? 'intro-features-text--wide' : ''} dangerouslySetInnerHTML={safe(content.adventuresCtaText)} />}
        <button className="cta-button" onClick={() => setIsQuestionOpen(true)}>
          Задать вопрос
        </button>
      </section>
      <BookingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <QuestionModal isOpen={isQuestionOpen} onClose={() => setIsQuestionOpen(false)} pageTitle="Путешествия" />
    </main>
  );
};

export default Adventures;
